<template>
  <v-row>
    <v-col cols="12">
      <div class="title mb-1">Pipeline</div>
      <v-divider />
    </v-col>
    <v-col cols="12" sm="6" md="3">
      <amount-widget 
        type="opportunity"
        :loading="loading"
        :value="pipeline.OPPORTUNITY.value"
        :quantity="pipeline.OPPORTUNITY.quantity"
      />
    </v-col>

    <v-col cols="12" sm="6" md="3">
      <amount-widget 
        type="pending"
        :loading="loading"
        :value="pipeline.PENDING.value"
        :quantity="pipeline.PENDING.quantity"
      />
    </v-col>

    <v-col cols="12" sm="6" md="3">
      <amount-widget 
        type="followup"
        :loading="loading"
        :value="pipeline.FOLLOW_UP.value"
        :quantity="pipeline.FOLLOW_UP.quantity"
      />
    </v-col>

    <v-col cols="12" sm="6" md="3">
      <amount-widget 
        type="win"
        :loading="loading"
        :value="pipeline.WIN.value"
        :quantity="pipeline.WIN.quantity"
        :footer1="pipeline.LOSS.value"
      />
    </v-col>

    <v-col cols="12">
      <v-row no-gutters class="mb-1 align-center">
        <v-col class="flex-shrink-1 flex-grow-0 title pr-4">
          Metrics
        </v-col>
        <v-col>

          <date-range-picker
            ref="picker"
            v-model="dateRange"
            auto-apply
            :ranges="{
              'Last 30 Days': [$moment().subtract(30,'days').toDate(), $moment().toDate()],
              'This Month':   [$moment().startOf('month').toDate(), $moment().endOf('month').toDate()],
              'Last Month':   [$moment().subtract(1, 'month').startOf('month').toDate(), $moment().subtract(1, 'month').endOf('month').toDate()],
              'This Year':    [$moment().startOf('year').toDate(), $moment().endOf('year').toDate()],
              'Last Year':    [$moment().subtract(1, 'year').startOf('year').toDate(), $moment().subtract(1, 'year').endOf('year').toDate()],
            }"
          >
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ $moment(picker.startDate).format('L') }} - {{ $moment(picker.endDate).format('L') }}
            </template>
          </date-range-picker>

        </v-col>
      </v-row>
      <v-divider />
    </v-col>
    <v-col cols="12" lg="9">
      <v-card style="margin-top: 13px;" class="pa-3">
        <line-chart :datasets="datasets" :options="dataoptions" :height="229" />
      </v-card>
    </v-col>
    <v-col cols="12" sm="10" offset-sm="1" md="8" offset-md="2" offset-lg="0" lg="3">
      <v-row class="flex-lg-column mt-0">
        <v-col cols="12" sm="6" lg="12">
          <amount-widget
            type="win_rate"
            :loading="loadingMetrics"
            :value="winRate"
            :footer1="pipeline.WIN.metrics.quantity"
            :footer2="pipeline.LOSS.metrics.quantity"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="12">
          <amount-widget
            type="conversion_rate"
            :loading="loadingMetrics"
            :value="conversionRate"
            :footer1="totalWins + totalLosses"
            :footer2="totalWins"
          />
        </v-col>
      </v-row>
    </v-col>

    <v-col cols="12">
      <div class="title mb-1">Agents</div>
      <v-divider />
    </v-col>
    <v-col
      cols="3"
      v-for="agent in agents"
      :key="'agent' + agent.id"
    >
      <v-card>
        <v-card-title>{{ agent.full_name }}</v-card-title>
        <v-simple-table dense>
          <template v-slot:default>
            <tbody>
              <tr>
                <td class="pa-0" style="width: 120px;">
                  <div class="px-3 py-2" style="background-color: rgba(0,0,0,.04);">
                    Opportunities
                  </div>
                </td>
                <td class="pa-0">
                  <div class="px-3 py-2 grey--text text-center" style="background-color: rgba(0,0,0,.04);">
                    {{ dashboardData.pipelines['agent_' + agent.id].OPPORTUNITY.quantity }}
                  </div>
                </td>
                <td class="currency"> {{ dashboardData.pipelines['agent_' + agent.id].OPPORTUNITY.value | numberRounded }} </td>
              </tr>
              <tr>
                <td class="pa-0">
                  <div class="px-3 py-2" style="background-color: rgba(0,0,0,.04);">
                    Pending
                  </div>
                </td>
                <td class="pa-0">
                  <div class="px-3 py-2 grey--text text-center" style="background-color: rgba(0,0,0,.04);">
                    {{ dashboardData.pipelines['agent_' + agent.id].PENDING.quantity }}
                  </div>
                </td>
                <td class="currency"> {{ dashboardData.pipelines['agent_' + agent.id].PENDING.value | numberRounded }} </td>
              </tr>
              <tr>
                <td class="pa-0">
                  <div class="px-3 py-2" style="background-color: rgba(0,0,0,.04);">
                    Follow Up
                  </div>
                </td>
                <td class="pa-0">
                  <div class="px-3 py-2 grey--text text-center" style="background-color: rgba(0,0,0,.04);">
                    {{ dashboardData.pipelines['agent_' + agent.id].FOLLOW_UP.quantity }}
                  </div>
                </td>
                <td class="currency"> {{ dashboardData.pipelines['agent_' + agent.id].FOLLOW_UP.value | numberRounded }} </td>
              </tr>
              <tr>
                <td class="pa-0">
                  <div class="px-3 py-2" style="background-color: rgba(0,0,0,.04);">
                    Won
                  </div>
                </td>
                <td class="pa-0">
                  <div class="px-3 py-2 grey--text text-center" style="background-color: rgba(0,0,0,.04);">
                    {{ dashboardData.pipelines['agent_' + agent.id].WIN.quantity }}
                  </div>
                </td>
                <td class="currency"> {{ dashboardData.pipelines['agent_' + agent.id].WIN.value | numberRounded }} </td>
              </tr>
              <tr>
                <td class="pa-0">
                  <div class="px-3 py-2" style="background-color: rgba(0,0,0,.04);">
                    Lost
                  </div>
                </td>
                <td class="pa-0">
                  <div class="px-3 py-2 grey--text text-center" style="background-color: rgba(0,0,0,.04);">
                    {{ dashboardData.pipelines['agent_' + agent.id].LOSS.quantity }}
                  </div>
                </td>
                <td class="currency"> {{ dashboardData.pipelines['agent_' + agent.id].LOSS.value | numberRounded }} </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </v-col>
    <v-col class="col-12">
      <v-row class="px-2">
        <v-col cols="3" lg="3" class="pa-0">
          <v-file-input
              label="Large Logo Upload"
              ref="large_logo"
              @change="downloadLargeLogo"
          ></v-file-input>
        </v-col>
        <v-col cols="3" lg="3" class="pa-0">
          <v-file-input
              label="Small Logo Upload"
              ref="small_logo"
              @change="downloadSmallLogo"
          ></v-file-input>
        </v-col>
      </v-row>
      <v-row class="px-2">
        <v-col v-if="large_logo" cols="3" lg="3" class="pa-0">
          <v-img :src="base_url+'logos/'+large_logo" max-width="180" class="mx-auto mb-1" />
        </v-col>
        <v-col v-if="small_logo" cols="3" lg="3" class="pa-0">
          <v-img :src="base_url+'logos/'+small_logo" max-width="180" class="mx-auto mb-1" />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import AmountWidget from '@/views/dashboard/AmountWidget'
import LineChart from '@/components/charts/LineChart'

export default {
  name: 'AdminDashboardIndex',
  components: {
    AmountWidget,
    LineChart
  },
  mounted () {
    //this.fillData()
    this.getDataFromApi()
    this.getLogos()
  },
  data () {
    return {
      loading: true,
      loadingMetrics: true,

      dashboardData: null,
      dateRange: {
        startDate: this.$moment().subtract(30,'days').toDate(),
        endDate: this.$moment().toDate()
      },
      datasets: null,
      small_logo:null,
      large_logo:null,
      base_url: process.env.VUE_APP_API_BASE_URL
    }
  },
  watch: {
    dateRange: {
      handler: function() {
        this.getDataFromApi()
      },
      deep: true
    }
  },
  computed: {
    user () {
      return this.$store.getters.user
    },
    partnership_id () {
      if(!this.$store.getters.user.partnership) return null
      return this.$store.getters.user.partnership.id
    },
    pipeline () {
      if(this.dashboardData) return this.dashboardData.pipelines.all
      return {
        OPPORTUNITY: {metrics: {}},
        PENDING: {metrics: {}},
        FOLLOW_UP: {metrics: {}},
        WIN: {metrics: {}},
        LOSS: {metrics: {}}
      }
    },
    agents () {
      if(this.dashboardData) return this.dashboardData.agents
      return []
    },
    winRate () {
      if(!this.dashboardData) return '-'
      let winQuantity = this.pipeline.WIN.metrics.quantity
      let lossQuantity = this.pipeline.LOSS.metrics.quantity
      if(winQuantity + lossQuantity == 0) return '-'

      return Math.round(winQuantity / ( winQuantity + lossQuantity ) * 100)
    },
    conversionRate () {
      if(!this.dashboardData) return '-'
      let winValue = this.pipeline.WIN.metrics.value
      let lossValue = this.pipeline.LOSS.metrics.value
      if(winValue + lossValue == 0) return '-'

      return Math.round(winValue / (winValue + lossValue) * 100)
    },
    totalWins () {
      if(!this.dashboardData) return 0
      return this.pipeline.WIN.metrics.value
    },
    totalLosses () {
      if(!this.dashboardData) return 0
      return this.pipeline.LOSS.metrics.value
    },
    dataoptions () {
      let ref = this

      return {
        legend: {
          display: true
        },
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: 'day',
            },
            ticks: {
              min: ref.$moment(ref.dateRange.startDate).toDate(),
              max: ref.$moment(ref.dateRange.endDate).toDate()
            },
            distribution: 'linear'
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function(label) {
                return label/1000+'k';
              }
            }
          }]
        },
        tooltips: { 
          callbacks: { 
            label: function(tooltipItem) 
            {
              let cleaned = ('' + tooltipItem.value).replace(/[^0-9.]/g, '')
              if(cleaned == '') return '0.00'
              let numberAmount = Math.round( parseFloat(cleaned) )
              return '$' + new Intl.NumberFormat('en-US', {minimumFractionDigits: 0}).format(numberAmount.toFixed(2))
            },
            title: function(tooltipItems) {
              return ref.$moment(tooltipItems[0].xLabel).format('L')
            }
          }
        }
      }
    }
  },
  methods: {
    getLogos(){
      if ( !this.partnership_id) {
        this.$swal.fire('Error', 'Unable to load partnership dashboard', 'error')
        return;
      }
      let requestUrl =  'partnerships/get-logos';
      const formData = new FormData();
      formData.append('partnership_id', this.partnership_id);
      this.$http({
        method: 'post',
        url: requestUrl,
        data: formData
      })
      .then(response => {
        this.large_logo = response.data.large_logo;
        this.small_logo = response.data.small_logo;
      })
    },
    downloadLargeLogo(file) {
      if (!file || !this.partnership_id) {
        this.$swal.fire('Error', 'Unable to load partnership dashboard', 'error')
        return;
      }
      const formData = new FormData();
      formData.append('document', file);
      formData.append('partnership_id', this.partnership_id);
      formData.append('logo_type', 'large_logo');

      let requestUrl =  'partnerships/logo-upload';
      this.$http({
        method: 'post',
        url: requestUrl,
        data: formData
      })
      .then(response => {
        this.large_logo = response.data;
      })

      .catch(err => {
        console.log(err.response)
        this.$swal.fire('Error', 'There was a problem saving the logo', 'error')
      })
    },

    downloadSmallLogo(file) {
      if (!file || !this.partnership_id) {
        this.$swal.fire('Error', 'Unable to load partnership dashboard', 'error')
        return;
      }
      const formData = new FormData();
      formData.append('document', file);
      formData.append('partnership_id', this.partnership_id);
      formData.append('logo_type', 'small_logo');

      let requestUrl =  'partnerships/logo-upload';

      this.$http({
        method: 'post',
        url: requestUrl,
        data: formData
      })
      .then(response => {
        this.small_logo = response.data;
      })
      .catch(err => {
        console.log(err.response)
        this.$swal.fire('Error', 'There was a problem saving the logo', 'error')
      })
    },
    getDataFromApi() {
      this.loadingMetrics = true
      if(!this.partnership_id) {
        this.$swal.fire('Error', 'Unable to load partnership dashboard', 'error')
        return
      }

      this.$http.get('partnerships/' + this.partnership_id + '/dashboard?start_date=' + this.$moment(this.dateRange.startDate).format('YYYY-MM-DD') + '&end_date=' + this.$moment(this.dateRange.endDate).format('YYYY-MM-DD'))
        .then(response => {
          this.dashboardData = response.data
          this.loading = false
          this.loadingMetrics = false
          this.fillData()
        })
        .catch(err => {
          if(err.response.status == 401) return
          
          console.log(err.response)
          this.$swal.fire('Error', 'There was an error loading your dashboard. Please try refreshing the page.', 'error')
        })
    },
    fillData () {
      let opportunityData = {};
      for(let i = 0; i < this.pipeline.OPPORTUNITY.metrics.coords.length; i++) {
        let coords = this.pipeline.OPPORTUNITY.metrics.coords[i]
        opportunityData[ coords.x ] = parseInt(coords.y) + parseInt( (opportunityData[ coords.x ] || 0) ) 
      }
      for(let i = 0; i < this.pipeline.PENDING.metrics.coords.length; i++) {
        let coords = this.pipeline.PENDING.metrics.coords[i]
        opportunityData[ coords.x ] = parseInt(coords.y) + parseInt( (opportunityData[ coords.x ] || 0) ) 
      }
      for(let i = 0; i < this.pipeline.FOLLOW_UP.metrics.coords.length; i++) {
        let coords = this.pipeline.FOLLOW_UP.metrics.coords[i]
        opportunityData[ coords.x ] = parseInt(coords.y) + parseInt( (opportunityData[ coords.x ] || 0) ) 
      }
      

      let opportunityCoords = []
      for(let i = 0; i < Object.keys(opportunityData).length; i++) {
        let key = Object.keys(opportunityData)[i]
        opportunityCoords.push({
          x: key,
          y: opportunityData[key]
        })
      }
      

      let sortedOpportunityCoords = opportunityCoords.sort( (a, b) => {
        if(a.x < b.x) return -1
        if(a.x > b.x) return 1
        return 0
      })

      console.log(sortedOpportunityCoords)

      this.datasets = [
        {
          label: 'Opportunities',
          data: sortedOpportunityCoords,
          lineTension: 0
        },
        {
          label: 'Wins',
          data: this.pipeline.WIN.metrics.coords,
          lineTension: 0,
          backgroundColor: 'rgb(0,200,0,.2)'
        }
      ]
    }
  }
}
</script>